'use client'

import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { CustomToolbarTableModule } from '@repo/clients-matters-tables/CustomToolbarTableModule'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRenderHeaderFilterProps,
  GridSortModel,
  GridColumnMenu,
  GridColumnMenuProps,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import {
  DateDataGridFilter,
  TextFieldDataGridFilter
} from '@repo/clients-matters-tables/DataGridTableCustomFilters'
import { editBundle } from './editBundle'
import { formatDate } from '@repo/utils/time'
import { MoreMenuCard } from './MoreMenuCard'
import { TableClearFilters } from '@repo/ui/TableClearFilters'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMatterBundleStore } from '@repo/store/useMatterBundleStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useNavigate } from 'react-router-dom'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import { useWindowSize } from '@uidotdev/usehooks'
import { v4 as uuidv4 } from 'uuid'
import defaultDocumentBundlesTableConfig from '@repo/store/defaultDocumentBundlesTableConfig'
import MUIPaper from '@mui/material/Paper'
import MUITypography from '@mui/material/Typography'

const parseMaybeJson = (val: unknown) => {
  if (!val) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch {
      return {}
    }
  }
  if (typeof val === 'object') return val as Record<string, any>
  return {}
}

const normalizeToLocalDateOnly = (d: Date | string | null | undefined): Date | null => {
  if (!d) return null
  const dt = typeof d === 'string' ? new Date(d) : d
  if (Number.isNaN(dt.getTime())) return null
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
}

export const DocumentBundlesModule = () => {
  const apiRef = useGridApiRef()
  const navigate = useNavigate()

  const { currentMatter } = useMattersStore()
  const { fetchMatterBundles, matterBundles } = useMatterBundleStore()
  const { Matters: bundlesPreferences, updateUserPreference } = useUserPreferencesStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { enableBundledocs } = useLookupsStore()
  const {
    documentBundlesTableFilterCount,
    documentBundlesTableFilters,
    resetDocumentBundlesTableFilters,
    setDocumentBundlesTableFilters,
    setDocumentBundlesTableFiltersFromPreferences
  } = useDataGridFilterStore()
  const { height: windowHeight } = useWindowSize()

  const activeFilters = documentBundlesTableFilters
  const activeFilterCount = documentBundlesTableFilterCount

  const OPERATOR_KEYS = new Set([
    'numberComparisonOperator',
    'dateComparisonOperator',
    'comparisonOperator',
    'operator'
  ])

  const isEmptyPrimitive = (x: unknown) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '')

  const isFilterValueActive = (v: any): boolean => {
    if (isEmptyPrimitive(v)) return false
    if (Array.isArray(v)) return v.some((item) => !isEmptyPrimitive(item))
    switch (typeof v) {
      case 'boolean':
        return true
      case 'number':
        return true
      case 'string':
        return v.trim() !== ''
      case 'object': {
        if ('value' in v) return !isEmptyPrimitive((v as any).value)
        if ('date' in v) return !isEmptyPrimitive((v as any).date)
        return Object.entries(v as Record<string, any>).some(
          ([k, val]) => !OPERATOR_KEYS.has(k) && isFilterValueActive(val)
        )
      }
      default:
        return false
    }
  }

  const areFiltersEffectivelyEmpty = (f: Record<string, any> | null | undefined) => {
    if (!f) return true
    return !Object.values(f).some((v) => isFilterValueActive(v))
  }

  const emptyOutFilters = (f: Record<string, any> | null | undefined) => {
    if (!f) return {}
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(f)) {
      if (v && typeof v === 'object') {
        if ('date' in v || 'dateComparisonOperator' in v) {
          out[k] = { date: null, dateComparisonOperator: 0 }
        } else {
          out[k] = null
        }
      } else {
        out[k] = null
      }
    }
    return out
  }

  const prefsHydratedRef = useRef(false)
  const userClearedRef = useRef(false)

  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({})
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({ left: [], right: [] })
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)

  // Resolve user preferences for this table
  const activePref = useMemo(() => {
    return bundlesPreferences?.MatterBundle_GetByClientNoAndMatterNo ?? null
  }, [bundlesPreferences])

  const resolvedConfig = useMemo(() => {
    const rv = activePref?.resolvedValue ?? activePref?.userValue ?? activePref?.defaultValue ?? {}
    return parseMaybeJson(rv)
  }, [activePref])

  const activePrefId = activePref?.userConfigurationTypeId ?? null

  // Hydrate filters from preferences
  useEffect(() => {
    const prefBag = resolvedConfig?.documentBundlesTableFilters ?? null
    const storeEmpty = areFiltersEffectivelyEmpty(activeFilters?.filters)
    const prefsEmpty = areFiltersEffectivelyEmpty(prefBag)

    if (prefsHydratedRef.current) return

    if (!storeEmpty) {
      prefsHydratedRef.current = true
      return
    }

    if (!prefsEmpty) {
      setDocumentBundlesTableFiltersFromPreferences({ filters: prefBag ?? null })
      prefsHydratedRef.current = true
      return
    }

    prefsHydratedRef.current = true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedConfig?.documentBundlesTableFilters, activeFilters?.filters, setDocumentBundlesTableFiltersFromPreferences])

  // Hydrate column config from preferences
  useEffect(() => {
    const fallback = defaultDocumentBundlesTableConfig || {}
    setColumnOrder(resolvedConfig.columnOrder ?? fallback.columnOrder ?? [])
    setColumnVisibilityModel(resolvedConfig.columnVisibilityModel ?? fallback.columnVisibilityModel ?? {})
    setColumnWidths(resolvedConfig.columnWidths ?? fallback.columnWidths ?? {})
    setPinnedColumns({
      left: resolvedConfig.columnsPinnedLeft ?? fallback.columnsPinnedLeft ?? [],
      right: resolvedConfig.columnsPinnedRight ?? fallback.columnsPinnedRight ?? []
    })
    setSortModel(resolvedConfig.columnSort ?? fallback.columnSort ?? [{ field: 'createdDate', sort: 'desc' }])
  }, [resolvedConfig])

  const updateTableConfigPreference = useCallback(
    (fieldsToChange: Record<string, any>) => {
      if (!activePrefId) return
      const next = {
        ...resolvedConfig,
        ...fieldsToChange
      }
      updateUserPreference({
        userConfigurationTypeId: activePrefId,
        value: JSON.stringify(next),
        convertJsonToString: false,
        categoryName: 'Matters'
      })
    },
    [resolvedConfig, activePrefId, updateUserPreference]
  )

  // Fetch bundles when matter changes
  useEffect(() => {
    if (!currentMatter?.clientNo || !currentMatter?.matterNo) return

    const load = async () => {
      setLoading(true)
      try {
        await fetchMatterBundles({
          clientNo: currentMatter.clientNo,
          matterNo: currentMatter.matterNo
        })
      } catch (err) {
        console.error('Error fetching matter bundles:', err)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [fetchMatterBundles, currentMatter?.clientNo, currentMatter?.matterNo])

  // Adjust table height when window resizes
  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 216 : 146
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  // Column event handlers — persist preferences
  const onColumnOrderChange = useCallback(() => {
    if (!apiRef.current) return
    const orderedFields = apiRef.current
      .getAllColumns()
      .filter((col) => !col.field.startsWith('__'))
      .map((col) => col.field)
    setColumnOrder(orderedFields)
    updateTableConfigPreference({ columnOrder: orderedFields })
  }, [apiRef, updateTableConfigPreference])

  const onColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(model)
      updateTableConfigPreference({ columnVisibilityModel: model })
    },
    [updateTableConfigPreference]
  )

  const onColumnWidthChange = useCallback(
    (params: any) => {
      const next = { ...columnWidths, [params.colDef.field]: params.width }
      setColumnWidths(next)
      updateTableConfigPreference({ columnWidths: next })
    },
    [columnWidths, updateTableConfigPreference]
  )

  const onPinnedColumnsChange = useCallback(
    (pinned: GridPinnedColumnFields) => {
      setPinnedColumns(pinned)
      updateTableConfigPreference({
        columnsPinnedLeft: pinned.left ?? [],
        columnsPinnedRight: pinned.right ?? []
      })
    },
    [updateTableConfigPreference]
  )

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model)
      updateTableConfigPreference({ columnSort: model })
    },
    [updateTableConfigPreference]
  )

  // Client-side filtering
  const filteredRows = useMemo(() => {
    const f = activeFilters?.filters || {}
    let rows: any[] = Array.isArray(matterBundles) ? [...matterBundles] : []

    // Filter by clientNo / matterNo (matter column)
    if (f.clientNo) {
      rows = rows.filter((r) => String(r.clientNo ?? '').toLowerCase().includes(String(f.clientNo).toLowerCase()))
    }
    if (f.matterNo) {
      rows = rows.filter((r) => String(r.matterNo ?? '').includes(String(f.matterNo)))
    }

    // Filter by description
    if (f.description) {
      rows = rows.filter((r) => (r.description ?? '').toLowerCase().includes(f.description.toLowerCase()))
    }

    // Filter by createdBy
    if (f.createdBy) {
      rows = rows.filter((r) => (r.createdBy ?? '').toLowerCase().includes(f.createdBy.toLowerCase()))
    }

    // Filter by createdDate
    if (f.createdDate?.date) {
      const filterDate = normalizeToLocalDateOnly(f.createdDate.date)
      if (filterDate) {
        const op = f.createdDate.dateComparisonOperator ?? 0
        rows = rows.filter((r) => {
          const rowDate = normalizeToLocalDateOnly(r.createdDate)
          if (!rowDate) return false
          const filterTime = filterDate.getTime()
          const rowTime = rowDate.getTime()
          if (op === 0) return rowTime === filterTime             // equal to
          if (op === 1) return rowTime >= filterTime             // after or equal
          if (op === 2) return rowTime <= filterTime             // before or equal
          return true
        })
      }
    }

    return rows
  }, [matterBundles, activeFilters?.filters])

  // Client-side sort
  const sortedRows = useMemo(() => {
    if (!sortModel.length) return filteredRows
    const { field, sort } = sortModel[0]
    return [...filteredRows].sort((a, b) => {
      const av = a[field] ?? ''
      const bv = b[field] ?? ''
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sort === 'asc' ? cmp : -cmp
    })
  }, [filteredRows, sortModel])

  // Client-side pagination
  const pageSize = activeFilters?.filters?.pageSize ?? 25
  const startAtIndex = activeFilters?.filters?.startAtIndex ?? 0
  const paginatedRows = useMemo(() => {
    return sortedRows.slice(startAtIndex, startAtIndex + pageSize)
  }, [sortedRows, startAtIndex, pageSize])

  const handleClearAll = useCallback(() => {
    userClearedRef.current = true
    resetDocumentBundlesTableFilters()
    const currentPrefFilters = resolvedConfig?.documentBundlesTableFilters
    const emptied = emptyOutFilters(currentPrefFilters ?? activeFilters?.filters ?? {})
    updateTableConfigPreference({ documentBundlesTableFilters: emptied })
  }, [resetDocumentBundlesTableFilters, resolvedConfig?.documentBundlesTableFilters, activeFilters?.filters, updateTableConfigPreference])

  const columns: any = [
    {
      field: 'matterNo',
      headerName: 'Matter',
      width: columnWidths?.['matterNo'] || 150,
      type: 'string',
      hideable: false,
      renderCell: (params: any) => (
        <ClientMatterDetailsPill
          matterNo={params.row.matterNo}
          clientNo={params.row.clientNo}
          sx={{ top: '3px' }}
        />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          filterType="matterNo"
          isDocumentBundlesTable
          {...params}
        />
      )
    },
    {
      field: 'createdDate',
      headerName: 'Created date',
      width: columnWidths?.['createdDate'] || 190,
      type: 'string',
      renderCell: (params: any) => (
        <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
          {params.value ? formatDate(params.value) : '-'}
        </MUITypography>
      ),
      renderHeaderFilter: () => (
        <DateDataGridFilter
          filterType="createdDate"
          isDocumentBundlesTable
        />
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      width: columnWidths?.['description'] || 300,
      type: 'string',
      flex: 1,
      renderCell: (params: any) => (
        <MUITypography
          variant="body2"
          onClick={() => editBundle(navigate, params.row)}
          sx={{
            mb: 0,
            fontSize: '0.875rem',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {params.value || '-'}
        </MUITypography>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          filterType="description"
          isDocumentBundlesTable
          {...params}
        />
      )
    },
    {
      field: 'createdBy',
      headerName: 'Created by',
      width: columnWidths?.['createdBy'] || 200,
      type: 'string',
      renderCell: (params: any) => (
        <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
          {params.value || '-'}
        </MUITypography>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          filterType="createdBy"
          isDocumentBundlesTable
          {...params}
        />
      )
    },
    {
      field: 'moreMenu',
      headerName: '',
      width: columnWidths?.['moreMenu'] || 56,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: (params: any) => <MoreMenuCard rowData={params.row} />
    }
  ].sort((a, b) => {
    const targetIndexA = columnOrder?.indexOf(a.field) ?? 0
    const targetIndexB = columnOrder?.indexOf(b.field) ?? 1
    if (targetIndexA > targetIndexB) return 1
    return -1
  })

  const getTogglableColumns = (cols: GridColDef[]) => {
    return cols
      .filter((column) => column.hideable !== false && column.field !== '__check__')
      .map((column) => column.field)
  }

  const paginationModel: GridPaginationModel = {
    page: Math.floor(startAtIndex / pageSize),
    pageSize
  }

  if (!enableBundledocs) return <MUIPaper data-testid="DocumentBundlesModule"></MUIPaper>

  return (
    <MUIPaper
      data-testid="DocumentBundlesModule"
      sx={{
        position: 'relative',
        fontSize: '0.875rem',
        height: `calc(${tableHeight}px - 20px)` || 'auto',
        width: '100%',
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        mt: 3,
        [`& .MuiDataGrid-root`]: { border: 'none' }
      }}
    >
      <TableClearFilters
        filterCount={activeFilterCount}
        onClear={handleClearAll}
        sx={{ position: 'absolute', top: '3px', right: '130px', zIndex: 2 }}
      />
      <DataGridPro
        apiRef={apiRef}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        disableVirtualization
        getRowId={(row) => `${row.matterBundleId ?? uuidv4()}`}
        headerFilters
        loading={loading}
        onColumnOrderChange={onColumnOrderChange}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onColumnWidthChange={onColumnWidthChange}
        onPaginationModelChange={(newModel) => {
          setDocumentBundlesTableFilters({ pageSize: newModel.pageSize })
          setDocumentBundlesTableFilters({ startAtIndex: newModel.page * newModel.pageSize })
        }}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={[5, 10, 25, 50]}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        pinnedColumns={pinnedColumns}
        rowCount={sortedRows.length}
        rows={paginatedRows}
        slotProps={{
          columnsManagement: { getTogglableColumns },
          loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
        }}
        sortModel={sortModel}
        sx={{
          [`.MuiDataGrid-root`]: { border: 'none' },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' },
          [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
          [`& .MuiDataGrid-cell`]: {
            alignContent: 'center',
            [`& .MuiTypography-root`]: { top: 'unset' }
          },
          [`& .MuiTablePagination-selectLabel`]: { display: 'block !important' },
          [`& .MuiTablePagination-input`]: {
            display: 'inline-flex !important',
            mb: 0,
            ml: 0,
            mt: '8px',
            fontSize: '0.875rem',
            [`& .MuiTablePagination-select`]: {
              padding: '0 !important',
              ml: '4px',
              mr: '20px',
              minWidth: 'none',
              [`&:focus`]: { backgroundColor: 'transparent' }
            },
            [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
          }
        }}
        slots={{
          columnMenu: (props: GridColumnMenuProps) => (
            <GridColumnMenu
              {...props}
              // @ts-ignore
              sx={{ '.MuiTypography-body1': { mb: 0 } }}
              slots={{ columnMenuFilterItem: null }}
            />
          ),
          headerFilterMenu: null,
          toolbar: () => <CustomToolbarTableModule isDocumentBundlesTable apiRef={apiRef} />
        }}
      />
    </MUIPaper>
  )
}